import { scrollToElementWithOffset, sleep } from ".";
import { TAnswerFlow } from "./beauty/types";
import {
  InputTextExtraProps,
  TSurveyQuestionType,
  TTextElement,
  TTextMatchOptions,
} from "./types";

export class Question {
  // private readonly _isRequireVisible: boolean;
  private readonly questionNode: HTMLElement;
  private readonly questionType: TSurveyQuestionType;
  public readonly parentSection: string;

  constructor(
    wantedSectionName: string | RegExp | undefined,
    wantedQuestionLabel: string | RegExp,
    questionType: TSurveyQuestionType
    // isRequireVisible: boolean = true
  ) {
    this.questionType = questionType;
    // this._isRequireVisible = isRequireVisible;

    const { node, section } = this._findQuestionNode(
      wantedQuestionLabel,
      wantedSectionName
    );
    if (node === null) {
      throw new Error(
        `[Question] No question found for label: "${wantedQuestionLabel}" in section: "${
          wantedSectionName ?? "(any)"
        }"`
      );
    }

    this.questionNode = node;
    this.parentSection = section;
  }

  public getAnswer(): string | null {
    if (this.questionType === "text") {
      const input = this._getTextInputNode();
      const value = input.value.trim();
      return value === "" ? null : value;
    }

    if (this.questionType === "radio") {
      const selectedInput = this.questionNode
        .querySelector("[id$='WebQuestion']")
        ?.querySelector(
          "input[type='radio']:checked"
        ) as HTMLInputElement | null;

      if (selectedInput === null) return null;

      const answerLabelNode = selectedInput.nextElementSibling as HTMLElement;
      if (answerLabelNode === null) return null;

      return answerLabelNode.innerText;
    }

    throw new Error(
      "[Question.getAnswer] Question type is neither text or radio"
    );
  }

  public giveAnswer(answerText: string): void {
    if (this.questionType === "text") {
      this._fillText(answerText);
    } else if (this.questionType === "radio") {
      this._selectRadio(answerText);
    } else {
      throw new Error(
        "[Question.giveAnswer] Question type is neither text or radio"
      );
    }
  }

  private _getParentSectionName(questionNode: HTMLElement): string {
    let prevSibling: Element | null = questionNode.previousElementSibling;

    while (prevSibling) {
      const questionNumberOrderSpan = prevSibling.querySelector<HTMLElement>(
        "span[id$='QuestionOrder']"
      );

      if (
        questionNumberOrderSpan &&
        questionNumberOrderSpan.style.fontSize === "x-large" &&
        questionNumberOrderSpan.innerText.trim() === ""
      ) {
        const questionText = prevSibling.querySelector<HTMLElement>(
          "span[id$='QuestionText']"
        );
        if (questionText === null) {
          throw new Error("Question Text element is null");
        }
        return questionText.innerText.trim();
      }

      prevSibling = prevSibling.previousElementSibling;
    }

    throw new Error("No Parent Section name found");
  }

  private _findQuestionNode(
    wantedQuestionLabel: string | RegExp,
    wantedSectionName: string | RegExp | undefined
  ): {
    node: HTMLElement | null;
    section: string;
  } {
    const QUESTION_NODES = document.querySelector(
      "#MasterContentCell > table.BodyText > tbody"
    )!.children;

    for (const node of QUESTION_NODES) {
      const questionNode = node as HTMLElement;
      const labelNode = questionNode.querySelector<HTMLElement>(
        "[id$='QuestionText']"
      );
      if (!labelNode) continue;

      // const helpNode = questionNode.querySelector<HTMLElement>("span[id$='QuestionHelp']");
      // // const isNonRequired =
      // //   helpNode !== null &&
      // //   isMatchNodeText(helpNode, {
      // //     mode: "string",
      // //     value: "Based on your above answer, this question does not need to be answered",
      // //   });

      // // if (this.isRequireVisible && isNonRequired) continue;

      const textMatchOptions: TTextMatchOptions =
        wantedQuestionLabel instanceof RegExp
          ? {
              mode: "regex",
              pattern: wantedQuestionLabel,
            }
          : {
              mode: "string",
              value: wantedQuestionLabel,
              ignoreCase: true,
            };

      const isQuestionLabelMatch = isMatchNodeText(labelNode, textMatchOptions);

      if (!isQuestionLabelMatch) continue;

      const parentSectionName = this._getParentSectionName(questionNode);
      if (
        wantedSectionName === undefined ||
        (typeof wantedSectionName === "string" &&
          parentSectionName === wantedSectionName) ||
        (wantedSectionName instanceof RegExp &&
          wantedSectionName.test(parentSectionName))
      ) {
        return { node: questionNode, section: parentSectionName };
      }
    }

    return { node: null, section: "" };
  }

  private _getTextInputNode(): TTextElement {
    const input = this.questionNode
      .querySelector("[id$='WebQuestion']")
      ?.querySelector("textarea, input:not([type='hidden'])");

    if (input === null) {
      throw new Error(
        "[Question] Could not find input or textarea inside question node."
      );
    }

    return input as TTextElement;
  }

  private _fillText(
    textToWrite: string,
    options?: InputTextExtraProps
  ): boolean {
    if (this.questionType !== "text") {
      throw new Error("[Question.fillText] Called on a non-text question.");
    }

    const inputNode = this._getTextInputNode();
    inputNode.value = textToWrite;

    inputNode.dispatchEvent(new Event("change", { bubbles: true }));
    inputNode.dispatchEvent(new Event("blur", { bubbles: true }));

    if (options?.clickSubmit === true) {
      const submitButton =
        this.questionNode.querySelector<HTMLElement>("button.add-item");
      submitButton?.click();
    }

    return true;
  }

  private _selectRadio(optionText: string): boolean {
    if (this.questionType !== "radio") {
      throw new Error("[Question.selectRadio] Called on a non-radio question.");
    }

    const labelNodes = this.questionNode
      .querySelector("[id$='WebQuestion']")
      ?.querySelectorAll("label");

    if (!labelNodes) {
      throw new Error("[Question.selectRadio] No label nodes found.");
    }

    for (const label of labelNodes) {
      if (label.innerText.trim().startsWith(optionText)) {
        const input = label.parentElement?.querySelector<HTMLElement>(
          "input[type='radio']"
        );
        if (!input) {
          throw new Error(
            "[Question.selectRadio] Radio input not found in label."
          );
        }
        input.click();
        return true;
      }
    }

    return false;
  }
}

function isMatchNodeText(
  element: HTMLElement,
  options: TTextMatchOptions
): boolean {
  const text = element.innerText;

  if (!text) return false;

  if (options.mode === "regex") {
    return options.pattern.test(text);
  }

  const { value, ignoreCase = false } = options;

  if (ignoreCase) {
    return text.toLowerCase().startsWith(value.toLowerCase());
  }

  return text.startsWith(value);
}

export function inputText(
  questionText: string,
  textToWrite: string,
  options?: InputTextExtraProps
): HTMLElement | null {
  const questionNode = getQuestionNodeFromLabel(
    questionText,
    options?.isRequireVisible
  );
  if (questionNode == null) {
    return null;
  }

  const textNode = getCorrespondingTextNode(questionNode);
  if (textNode == null) {
    console.log("[input_text] Input/textarea node not found");
    return null;
  }

  textNode.value = textToWrite;

  const changeEvent = new Event("change", { bubbles: true });
  textNode.dispatchEvent(changeEvent);
  const blueEvent = new Event("blur", { bubbles: true });
  textNode.dispatchEvent(blueEvent);

  if (options?.clickSubmit === true) {
    const submitButton: HTMLButtonElement | null =
      questionNode.querySelector("button.add-item");
    if (submitButton === null) {
      console.warn(
        "[input_text] Submit button not found for question element:",
        questionNode
      );
      return questionNode;
    }
    console.log(submitButton);
    submitButton.click();
  }

  return questionNode;
}

export function getCorrespondingTextNode(
  questionNode: HTMLElement | null
): TTextElement | null {
  if (questionNode === null) {
    throw new Error("Question node is null");
  }

  return questionNode
    .querySelector("[id$='WebQuestion']")!
    .querySelector("textarea, input:not([type='hidden'])");
}

export function clickRadio(
  questionText: string,
  optionText: string
): HTMLElement | null {
  const questionNode = getQuestionNodeFromLabel(questionText);
  if (questionNode === null) {
    return null;
  }

  const labelNodes = questionNode
    .querySelector("[id$='WebQuestion']")!
    .querySelectorAll("label");

  for (const labelNode of labelNodes) {
    if (labelNode.innerText.trim().startsWith(optionText)) {
      const optionNode = labelNode.parentNode!.children[0] as HTMLOptionElement;
      optionNode.click();
      return questionNode;
    }
  }
  console.log(`[click_radio] Option text not found: "${optionText}"`);

  return questionNode;
}

// function _get_input_node_from_question_text(question_text: string) {
//   let temp = get_question_node_from_label(question_text);
//   return get_corresponding_text_node(temp);
// }

export function getQuestionNodeFromLabel(
  questionText: string,
  isRequireVisible?: boolean
): HTMLElement | null {
  const QUESTION_NODES = document.querySelector(
    "#MasterContentCell > table.BodyText > tbody"
  )!.children;
  for (const node of QUESTION_NODES) {
    if (
      _getQuestionText(node as HTMLElement)
        .toLowerCase()
        .includes(questionText.toLowerCase())
    ) {
      if (
        isRequireVisible === true &&
        node
          .querySelector("span[id$='QuestionHelp']")
          ?.innerHTML.includes(
            "Based on your above answer, this question does not need to be answered"
          ) === true
      ) {
        continue;
      }
      return node as HTMLElement;
    }
  }

  return null;
}

// function _get_next_sibling(mynode) {
//   let nodes = mynode.parentNode.querySelectorAll("tr");

//   for (let i = 0; i < nodes.length; ++i) {
//     if (nodes[i] == mynode) {
//       return nodes[i + 1];
//     }
//   }

//   return null;
// }

// function _get_root_node(question_text: string) {
//   let nodes = document.querySelectorAll("label[for^='ctl00_']");

//   for (let node of nodes) {
//     let main_node = node.parentNode.parentNode.parentNode.parentNode.parentNode.parentNode;
//     if (main_node.nodeName == "TD") {
//       let question_text_node = main_node.querySelector("span");
//       if (question_text_node.innerText.startsWith(question_text)) {
//         return main_node.parentNode;
//       }
//     }
//   }
//   return null;
// }

export function fillTimekeeping(): void {
  //   const title = getProjectTitle();
  //   const hoursRegex = /([0-9]+) *hr/gim;
  //   const minutesRegex = /([0-9]+) *min/gim;
  //   let hours = "0";
  //   let minutes = "0";
  //   const hoursMatch = hoursRegex.exec(title);
  //   const minutesMatch = minutesRegex.exec(title);
  //   if (hoursMatch) {
  //     hours = hoursMatch[1];
  //   }
  //   if (minutesMatch) {
  //     minutes = minutesMatch[1];
  //   }
  //   if (hours === "0" && minutes === "0") {
  //     return;
  //   }
  //   const hoursNode = document.querySelector(
  //     "#ctl00_ContentPlaceHolder_ReportEditor_gridTimeEntries_ctl02_ctl01"
  //   );
  //   if (!hoursNode) {
  //     throw new Error("Hours node is null");
  //   }
  //   let options = hoursNode.querySelectorAll("option");
  //   for (const option of options) {
  //     if (option.getAttribute("value") == hours) {
  //       option.selected = true;
  //       break;
  //     }
  //   }
  //   const minutesNode = document.querySelector(
  //     "#ctl00_ContentPlaceHolder_ReportEditor_gridTimeEntries_ctl02_ctl02"
  //   );
  //   if (!minutesNode) {
  //     throw new Error("Minute node is null");
  //   }
  //   options = minutesNode.querySelectorAll("option");
  //   for (const option of options) {
  //     if (option.getAttribute("value") == minutes) {
  //       option.selected = true;
  //       break;
  //     }
  //   }
  //   const event = new Event("change", { bubbles: true });
  //   hoursNode.dispatchEvent(event);
  //   minutesNode.dispatchEvent(event);
}

function _getQuestionText(questionNode: HTMLElement): string {
  const questionTextNode: HTMLElement = questionNode.querySelector(
    "[id$='QuestionText']"
  )!;
  return questionTextNode.innerText;
}

export function getProjectTitle(): string {
  const projectNameNode: HTMLElement = document.querySelector(
    "[id$='_ProjectNameLabel']"
  )!;
  return projectNameNode.innerText;
}

export function getStoreLabel(): string {
  const storeNameNode: HTMLElement = document.querySelector(
    "[id$='_StoreNameLabel']"
  )!;
  return storeNameNode.innerText;
}

export function getStoreName(): string {
  const storeNameRegex = /\bt[0-9]{4}\b/i;
  const match = storeNameRegex.exec(getStoreLabel());
  if (!match) {
    throw new Error("store name match is null");
  }
  return match[0];
}

export function requireProject(
  requiredStoreName: string,
  requiredProjectNameRegex: RegExp,
  callback: <T>() => T
) {
  const storeName = getStoreName();
  if (requiredStoreName && storeName != requiredStoreName) {
    console.warn(
      `Failed store requirement. Required store "${requiredStoreName}", but current store is "${storeName}"`
    );
    return;
  }

  const projectTitle = getProjectTitle();
  if (!requiredProjectNameRegex.test(projectTitle)) {
    console.warn(
      `Failed title requirement. Required title ${requiredProjectNameRegex}, but current project is "${projectTitle}"`
    );
    return;
  }

  callback();
}

export function submitSaveWork() {
  document.getElementById("ctl00_ContentPlaceHolder_btnSaveWork")?.click();
}

export function submitCheckAndSaveWork() {
  document.getElementById("ctl00_ContentPlaceHolder_SaveButton")?.click();
}

export function scrollToFirstRequiredQuestion() {
  const elms = document.querySelectorAll<HTMLElement>(
    "[id$='_WebQuestion_webQuestionRv']"
  );
  for (const elm of elms) {
    const closestTrElement = elm.closest("tr");
    if (closestTrElement === null) {
      throw new Error("Grandparent 'tr' element not found");
    }

    if (elm.style.display !== "none") {
      scrollToElementWithOffset(closestTrElement);
      return;
    }
  }

  console.warn("No '* required' element found");
}

function extractAnswerFromFlow(
  flowNumber: number,
  flows: Record<number, TAnswerFlow>,
  storeLabel: string
): string | null {
  const flow = flows[flowNumber];
  if (flow.answerDependency === "directAnswer") {
    return flow.answer(storeLabel).toString();
  }

  const dependencyAnswers: string[] = [];
  for (const key of flow.dependsOn.keys) {
    const targetFlow = flows[key];
    console.log(
      `Getting answer from dependency question '${targetFlow.questionLabel}'`
    );

    const question = new Question(
      targetFlow.sectionName,
      targetFlow.questionLabel,
      targetFlow.questionType
    );
    const dependencyAnswer = question.getAnswer();
    console.log(`Dependency answer is: ${dependencyAnswer}\n`);

    if (dependencyAnswer === null) {
      throw new Error(
        `dependency answer is null for question '${flow.questionLabel}'`
      );
    }

    dependencyAnswers.push(dependencyAnswer);
  }

  const answer = flow.dependsOn.getAnswer(dependencyAnswers);
  if (answer === null) return null;

  return answer.toString();
}

export async function fillAnswerFlows(
  answerFlows: Record<number, TAnswerFlow>
) {
  const storeLabel = getStoreLabel();

  for (const [flowKey, flow] of Object.entries(answerFlows)) {
    let question: Question;
    try {
      question = new Question(
        flow.sectionName,
        flow.questionLabel,
        flow.questionType
      );
    } catch (error) {
      console.error(error);
      continue;
    }

    const answer = extractAnswerFromFlow(
      Number(flowKey),
      answerFlows,
      storeLabel
    );
    if (answer === null) {
      console.warn(
        `Projected answer is null for question '${flow.questionLabel}'`
      );
      continue;
    }

    question.giveAnswer(answer);

    await sleep(10);
  }
}
